import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { LogDay, LogLine } from "../components/logs/LogLine";
import { PageHeader } from "../components/shell/PageHeader";
import { Button } from "../components/ui/Button";
import { EmptyState, ErrorNotice, SkeletonRows } from "../components/ui/Feedback";
import { SearchField, SelectField } from "../components/ui/Input";
import { Metric, MicroLabel } from "../components/ui/Text";
import { ApiError, get } from "../lib/api";
import { isStationIdFilter } from "../lib/connectionAttempts";
import { count } from "../lib/format";
import {
  LEVEL_PRESETS,
  LEVEL_PRESET_LABELS,
  WINDOWS,
  WINDOW_LABELS,
  isSameDay,
  levelsFor,
  liveState,
  windowStart,
} from "../lib/logs";
import type { AppLogPage } from "../lib/types";

/**
 * What the server logged, without a shell on the host.
 *
 * ## What this page is for
 *
 * The question "what did the server do at 14:02" used to be answered over SSH
 * with `docker logs | pino-pretty`. This is that, in the panel: the same coloured
 * levels and trailing fields, filterable by station, level and text, and live
 * while nobody is reading further back.
 *
 * ## Why reading older lines pauses it
 *
 * Pages are cursors into a log that keeps growing at the top. Refetching the
 * first page while older ones are loaded would open a gap between them, so the
 * moment someone asks for older lines the view stops moving, and says so.
 *
 * ## Why debug is shown by default
 *
 * The table keeps debug (`LOG_DB_LEVEL`) precisely so it can be read. The filter
 * is one click away for when it is noise.
 */

const LIVE_POLL_MS = 3_000;
const PAGE_SIZE = 100;
const SEARCH_SETTLE_MS = 300;

export function Logs() {
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [isPaused, setPaused] = useState(false);
  const [expanded, setExpanded] = useState<string | undefined>(undefined);

  const preset = oneOf(LEVEL_PRESETS, params.get("level"), "all");
  const span = oneOf(WINDOWS, params.get("window"), "24h");
  const cpId = params.get("cpId") ?? "";
  const q = params.get("q") ?? "";

  const [stationDraft, setStationDraft] = useState(cpId);
  const [searchDraft, setSearchDraft] = useState(q);
  const isStationDraftValid = stationDraft === "" || isStationIdFilter(stationDraft);

  // The URL follows a click on a station, or Back; the fields have to follow the URL.
  useEffect(() => setStationDraft(cpId), [cpId]);
  // Only when they disagree: a settled search must not strip the space being typed.
  useEffect(() => setSearchDraft((draft) => (draft.trim() === q ? draft : q)), [q]);

  // Typing settles before it becomes a request: one query per pause, not per key.
  // Restarted on any URL change, not just the search's: `setFilter` writes from the
  // params this render saw, so a timer from before a level change would undo it.
  useEffect(() => {
    if (searchDraft.trim() === q) return;
    const timer = setTimeout(() => setFilter("q", searchDraft.trim()), SEARCH_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [searchDraft, q, params]);

  const lines = useInfiniteQuery({
    queryKey: ["logs", { preset, span, cpId, q }],
    queryFn: ({ pageParam }) =>
      get<AppLogPage>("/logs", {
        query: {
          limit: PAGE_SIZE,
          level: levelsFor(preset),
          from: windowStart(span, new Date()),
          cpId,
          q,
          before: pageParam,
        },
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
    placeholderData: (previous) => previous,
    // `query.state` is this filter's own query, never the placeholder.
    refetchInterval: (query) =>
      isPaused || (query.state.data?.pages.length ?? 0) > 1 ? false : LIVE_POLL_MS,
    refetchOnWindowFocus: (query) => !isPaused && (query.state.data?.pages.length ?? 0) <= 1,
  });

  const rows = lines.data?.pages.flatMap((page) => page.rows) ?? [];
  const { isReadingBack, isLive } = liveState({
    pageCount: lines.data?.pages.length ?? 0,
    isPlaceholder: lines.isPlaceholderData,
    isPaused,
  });
  /**
   * `error` and `fatal` only. Unstructured lines stay in the "errors" filter,
   * because a crash trace is one — but most of them are harmless noise (npm,
   * pino-pretty in development), and counting them would make this number red on
   * a healthy server.
   */
  const errors = rows.filter((row) => row.level === "error" || row.level === "fatal").length;
  const isFiltered = preset !== "all" || cpId !== "" || q !== "";
  const forbidden = lines.error instanceof ApiError && lines.error.isForbidden;

  function setFilter(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value === "") next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
    setExpanded(undefined);
  }

  function stationHref(station: string): string {
    const next = new URLSearchParams(params);
    next.set("cpId", station);
    return `?${next.toString()}`;
  }

  return (
    <>
      <PageHeader
        eyebrow="Audit"
        title={cpId === "" ? "Server logs" : `Server logs · ${cpId}`}
        metrics={
          <>
            <Metric label="Lines" value={count(rows.length)} hint="shown" />
            <Metric
              label="Errors"
              value={count(errors)}
              hint="shown"
              tone={errors > 0 ? "signal" : "muted"}
            />
          </>
        }
        actions={
          <Button
            variant="ghost"
            onClick={() => {
              if (isReadingBack) {
                // Back to the top: forget the older pages, which is what lets polling resume.
                setPaused(false);
                void queryClient.resetQueries({ queryKey: ["logs"] });
                return;
              }
              setPaused(!isPaused);
            }}
          >
            {isLive ? "Pause" : "Resume live"}
          </Button>
        }
        freshness={
          <MicroLabel>
            {isLive
              ? `Live · every ${LIVE_POLL_MS / 1000}s`
              : isReadingBack
                ? "Paused while reading older lines"
                : "Paused"}
          </MicroLabel>
        }
        lede="What the server logged, newest first, as pino wrote it — the lines docker logs shows, without a shell on the host. Click a line for the whole record. Kept for 30 days unless the deployment says otherwise; lines from before this page existed are marked imported."
      />

      {forbidden ? (
        <EmptyState
          title="The server log needs the View server logs permission."
          detail="The log carries operators' and drivers' email addresses, client addresses and payment references, so it is granted on its own rather than with the other views. Ask an administrator for a role that includes it."
        />
      ) : (
        <>
          <form
            onSubmit={(event) => event.preventDefault()}
            className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2 lg:grid-cols-4"
          >
            <SelectField
              label="Level"
              value={preset}
              onChange={(event) =>
                setFilter("level", event.target.value === "all" ? "" : event.target.value)
              }
              hint="Unstructured lines stay in: a crash trace is one"
            >
              {LEVEL_PRESETS.map((one) => (
                <option key={one} value={one}>
                  {LEVEL_PRESET_LABELS[one]}
                </option>
              ))}
            </SelectField>
            <SelectField
              label="Window"
              value={span}
              onChange={(event) =>
                setFilter("window", event.target.value === "24h" ? "" : event.target.value)
              }
            >
              {WINDOWS.map((one) => (
                <option key={one} value={one}>
                  {WINDOW_LABELS[one]}
                </option>
              ))}
            </SelectField>
            <SearchField
              label="Station id"
              value={stationDraft}
              placeholder="CP-0001"
              hint="Exact match"
              error={
                isStationDraftValid
                  ? undefined
                  : "A station id is letters, digits, dot, dash and underscore only."
              }
              onChange={(event) => {
                const next = event.target.value.trim();
                setStationDraft(next);
                if (next === "" || isStationIdFilter(next)) setFilter("cpId", next);
              }}
            />
            <SearchField
              label="Search"
              value={searchDraft}
              placeholder="order id, message, anything"
              hint="Message and every field"
              maxLength={200}
              onChange={(event) => setSearchDraft(event.target.value)}
            />
          </form>

          {lines.isError ? (
            <ErrorNotice error={lines.error} onRetry={() => void lines.refetch()} />
          ) : null}

          <div className="flex flex-col gap-4">
            {lines.isPending ? (
              <table className="w-full">
                <tbody>
                  <SkeletonRows rows={10} columns={4} />
                </tbody>
              </table>
            ) : (
              <ol
                aria-label="Log lines"
                aria-busy={lines.isPlaceholderData}
                className={`m-0 list-none p-0 transition-opacity duration-fast ease-swiss ${lines.isPlaceholderData ? "opacity-50" : ""}`}
              >
                {rows.map((row, index) => {
                  const previous = rows[index - 1];
                  return (
                    <Fragment key={row.id}>
                      {previous === undefined || !isSameDay(previous.ts, row.ts) ? (
                        <LogDay iso={row.ts} />
                      ) : null}
                      <LogLine
                        entry={row}
                        isExpanded={expanded === row.id}
                        onToggle={() => setExpanded(expanded === row.id ? undefined : row.id)}
                        stationHref={stationHref}
                      />
                    </Fragment>
                  );
                })}
              </ol>
            )}

            {!lines.isPending && rows.length === 0 ? (
              <EmptyState
                title={
                  isFiltered
                    ? "No lines match these filters in this window."
                    : "Nothing logged in this window."
                }
                detail="Lines reach this page about a second after the server writes them. If the server itself is down, or cannot reach its database, they only exist in `docker logs` — this page cannot record its own outage."
              />
            ) : null}

            {lines.hasNextPage && !lines.isPlaceholderData ? (
              <div>
                <Button
                  variant="secondary"
                  pending={lines.isFetchingNextPage}
                  onClick={() => void lines.fetchNextPage()}
                >
                  Load older lines
                </Button>
              </div>
            ) : rows.length > 0 ? (
              <MicroLabel>Start of the window</MicroLabel>
            ) : null}
          </div>
        </>
      )}
    </>
  );
}

function oneOf<T extends string>(values: readonly T[], raw: string | null, fallback: T): T {
  return values.find((value) => value === raw) ?? fallback;
}
